"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Bell, BellOff } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { useNotifications, useMarkNotificationAsSeen } from "@/features/notifications/hooks";
import type { Notification } from "@/features/notifications/types";
import { getNotificationActor, getNotificationHref } from "@/features/notifications/utils";
import { Popover, Spinner } from "./ui";
import "./account.css";

/**
 * THE NOTIFICATION TRAY.
 *
 * Signed out it renders nothing, for the same reason the wallet does.
 *
 * It reads the app's existing `useNotifications` hook rather than adding a
 * second fetch path — React Query dedupes it against the `/notifications`
 * page, and the hook is already `enabled`-gated on the token.
 *
 * THE UNREAD COUNT IS CAPPED AT 9+. A three-digit badge on a 34px control
 * stops being a count and becomes a shape; past a handful the exact number
 * changes nothing about what you do next.
 *
 * Opening a row marks it seen through the existing mutation, which
 * invalidates the query — so the badge and the page stay in step without this
 * component holding any state of its own.
 */

const PREVIEW = 6;
export default function NotificationsMenu(t0) {
  const $ = _c(60);
  const {
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== lang) {
    t2 = p => `/${lang}${p}`;
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const href = t2;
  const token = useAuthStore(_temp);
  const {
    data,
    isLoading
  } = useNotifications(1);
  const markSeen = useMarkNotificationAsSeen();
  if (!token) {
    return null;
  }
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== data?.data?.notifications || $[5] !== href || $[6] !== isFa || $[7] !== isLoading || $[8] !== lang || $[9] !== markSeen || $[10] !== t) {
    const items = (data?.data?.notifications ?? []) as Notification[];
    const unread = items.filter(_temp2).length;
    let t10;
    let t11;
    let t12;
    if ($[19] !== isFa || $[20] !== t || $[21] !== unread) {
      const num = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
      T0 = Popover;
      t3 = "end";
      t4 = "v2-acct-menu v2-notif-menu";
      if ($[30] !== t) {
        t5 = t("\u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627", "Notifications");
        $[30] = t;
        $[31] = t5;
      } else {
        t5 = $[31];
      }
      t6 = t13 => {
        const {
          open,
          toggle,
          id
        } = t13;
        return <button type="button" className="v2-notif v2-focus" onClick={toggle} aria-expanded={open} aria-controls={id} aria-label={unread > 0 ? `${t("\u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627", "Notifications")} — ${num.format(unread)}` : t("\u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627", "Notifications")} data-open={open || undefined} data-unread={unread > 0 || undefined}><Bell aria-hidden={true} />{unread > 0 ? <span className="v2-notif-dot" aria-hidden={true}>{unread > 9 ? `${num.format(9)}+` : num.format(unread)}</span> : null}</button>;
      };
      t10 = "v2-notif-head";
      let t14;
      if ($[32] !== t) {
        t14 = t("\u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627", "Notifications");
        $[32] = t;
        $[33] = t14;
      } else {
        t14 = $[33];
      }
      if ($[34] !== t14) {
        t11 = <span className="v2-notif-k">{t14}</span>;
        $[34] = t14;
        $[35] = t11;
      } else {
        t11 = $[35];
      }
      t12 = unread > 0 ? <span className="v2-chip v2-chip--live">{num.format(unread)}</span> : null;
      $[19] = isFa;
      $[20] = t;
      $[21] = unread;
      $[22] = T0;
      $[23] = t10;
      $[24] = t11;
      $[25] = t12;
      $[26] = t3;
      $[27] = t4;
      $[28] = t5;
      $[29] = t6;
    } else {
      T0 = $[22];
      t10 = $[23];
      t11 = $[24];
      t12 = $[25];
      t3 = $[26];
      t4 = $[27];
      t5 = $[28];
      t6 = $[29];
    }
    if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12) {
      t7 = <div className={t10}>{t11}{t12}</div>;
      $[36] = t10;
      $[37] = t11;
      $[38] = t12;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="v2-pop-sep" />;
      $[40] = t8;
    } else {
      t8 = $[40];
    }
    t9 = isLoading ? <p className="v2-notif-empty"><Spinner /></p> : items.length === 0 ? <p className="v2-notif-empty"><BellOff aria-hidden={true} />{t("\u0627\u0639\u0644\u0627\u0646 \u062A\u0627\u0632\u0647\u200C\u0627\u06CC \u0646\u062F\u0627\u0631\u06CC\u062F.", "Nothing new.")}</p> : items.slice(0, PREVIEW).map(n_0 => {
      const actor = getNotificationActor(n_0);
      return <Link className={`v2-notif-row${n_0.read ? "" : " v2-notif-row--new"}`} key={n_0.id} href={getNotificationHref(n_0, lang) ?? href("/notifications")} role="menuitem" onClick={() => {
        if (!n_0.read) {
          markSeen.mutate(n_0.id);
        }
      }}><span className="v2-notif-mark" aria-hidden={true} /><span className="v2-notif-text"><b>{n_0.title}</b><span>{n_0.body}</span>{actor ? <small className="v2-notif-who"><bdi>{actor.name}</bdi>{actor.username ? <bdi dir="ltr">@{actor.username}</bdi> : null}</small> : null}</span></Link>;
    });
    $[4] = data?.data?.notifications;
    $[5] = href;
    $[6] = isFa;
    $[7] = isLoading;
    $[8] = lang;
    $[9] = markSeen;
    $[10] = t;
    $[11] = T0;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="v2-pop-sep" />;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  const t11 = href("/notifications");
  let t12;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Bell aria-hidden={true} />;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t) {
    t13 = t("\u0647\u0645\u0647\u200C\u06CC \u0627\u0639\u0644\u0627\u0646\u200C\u0647\u0627", "All notifications");
    $[43] = t;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t13) {
    t14 = <span>{t13}</span>;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] !== t11 || $[48] !== t14) {
    t15 = <Link className="v2-pop-item" href={t11} role="menuitem">{t12}{t14}</Link>;
    $[47] = t11;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== T0 || $[51] !== t15 || $[52] !== t3 || $[53] !== t4 || $[54] !== t5 || $[55] !== t6 || $[56] !== t7 || $[57] !== t8 || $[58] !== t9) {
    t16 = <T0 align={t3} className={t4} label={t5} trigger={t6}>{t7}{t8}{t9}{t10}{t15}</T0>;
    $[50] = T0;
    $[51] = t15;
    $[52] = t3;
    $[53] = t4;
    $[54] = t5;
    $[55] = t6;
    $[56] = t7;
    $[57] = t8;
    $[58] = t9;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  return t16;
}
function _temp2(n) {
  return !n.read;
}
function _temp(s) {
  return s.token;
}